import { useEffect, useState } from 'react';
import { fetchCancelable, executeCancel, pointPaymentCancel } from '../services/walletService';

function generateIdempotencyKey() {
  try { return crypto.randomUUID(); }
  catch (_) {
    return 'xxxx-xxxx-4xxx-yxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }
}

const fmt = (n) => (n ?? 0).toLocaleString();

const inputStyle = {
  height: '34px',
  padding: '0 10px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '13px',
  color: '#1f2937',
  outline: 'none',
  boxSizing: 'border-box',
  margin: 0,
  background: 'white',
};

const smallBtnBase = {
  height: '34px',
  padding: '0 12px',
  fontSize: '12px',
  fontWeight: 500,
  borderRadius: '6px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  margin: 0,
};

function CancelModal({ isOpen, orderId, userId, onClose, onSuccess }) {
  const [cancelable, setCancelable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [amounts, setAmounts] = useState({});
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || orderId == null) return;
    let cancelled = false;
    setLoading(true); setCancelable(null); setAmounts({}); setReason(''); setError('');
    fetchCancelable(orderId)
      .then((data) => {
        if (cancelled) return;
        setCancelable(data);
        const init = {};
        (data.items || []).forEach((item) => { init[item.adCampaignMetaId] = item.cancelableAmount; });
        setAmounts(init);
      })
      .catch((err) => { if (!cancelled) setError(err?.message || '정보를 불러올 수 없습니다.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, orderId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && isOpen && !submitting) onClose(); };
    if (isOpen) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, submitting]);

  const totalCancelAmount = Object.values(amounts).reduce((s, v) => s + (Number(v) || 0), 0);
  const epRefund = cancelable?.restorableEp != null ? Math.min(totalCancelAmount, cancelable.restorableEp) : null;
  const cpRefund = epRefund != null ? totalCancelAmount - epRefund : null;

  function handleAmountChange(metaId, value) {
    setAmounts((p) => ({ ...p, [metaId]: value === '' ? '' : Number(value) }));
  }
  function handleFull(item) { setAmounts((p) => ({ ...p, [item.adCampaignMetaId]: item.cancelableAmount })); }
  function handleHalf(item) { setAmounts((p) => ({ ...p, [item.adCampaignMetaId]: Math.floor(item.cancelableAmount / 2) })); }

  async function handleSubmit() {
    if (!cancelable) return;
    const items = (cancelable.items || [])
      .map((item) => ({ adCampaignMetaId: item.adCampaignMetaId, cancelAmount: Number(amounts[item.adCampaignMetaId]) || 0 }))
      .filter((it) => it.cancelAmount > 0);

    if (!items.length || totalCancelAmount === 0) { setError('취소 금액을 입력해주세요.'); return; }
    const over = (cancelable.items || []).find((item) => (Number(amounts[item.adCampaignMetaId]) || 0) > item.cancelableAmount);
    if (over) { setError(`[${over.name}] 취소 금액이 가능 금액을 초과했습니다.`); return; }
    if (totalCancelAmount > cancelable.cancelableAmount) { setError('총 취소 금액이 취소 가능 금액을 초과했습니다.'); return; }
    if (!reason.trim()) { setError('취소 사유를 입력해주세요.'); return; }

    setError(''); setSubmitting(true);
    try {
      const cancelResult = await executeCancel(orderId, items, reason, generateIdempotencyKey());
      if (userId && cancelResult?.data?.fullyCanceled) {
        try { await pointPaymentCancel(userId, orderId, reason, generateIdempotencyKey()); }
        catch (e) { console.error('포인트 결제 취소 오류:', e); }
      }
      onSuccess(); onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || '취소 처리 중 오류가 발생했습니다.');
    } finally { setSubmitting(false); }
  }

  if (!isOpen) return null;

  const isDisabled = submitting || loading || !cancelable;

  return (
    <div className="modal-overlay" data-testid="cancel-modal-overlay" onClick={() => { if (!submitting) onClose(); }}>
      <div
        className="modal-content"
        data-testid="cancel-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '520px', width: '90%', padding: 0, borderRadius: '12px', overflow: 'hidden' }}
      >
        {/* 헤더 */}
        <div style={{ padding: '18px 20px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937' }}>취소 실행</span>
          <button className="modal-close-btn" onClick={() => { if (!submitting) onClose(); }} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#9ca3af', padding: '2px 6px', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: '20px', maxHeight: '68vh', overflowY: 'auto' }}>
          {loading ? (
            <p style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af', fontSize: '14px', margin: 0 }}>불러오는 중...</p>
          ) : cancelable ? (
            <>
              {/* 주문 요약 */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', padding: '12px 16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                {[
                  { label: '최초 결제', value: `${fmt(cancelable.orderAmount)}원` },
                  { label: '누적 취소', value: `${fmt(cancelable.canceledAmount)}원` },
                  { label: '취소 가능', value: `${fmt(cancelable.cancelableAmount)}원`, primary: true },
                ].map(({ label, value, primary }) => (
                  <div key={label} style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px', fontWeight: 500 }}>{label}</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: primary ? '#6366f1' : '#374151' }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* 상품별 취소 금액 */}
              <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>상품별 취소 금액</div>
              <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                {(cancelable.items || []).map((item) => {
                  const val = amounts[item.adCampaignMetaId] ?? 0;
                  const disabled = item.cancelableAmount === 0;
                  return (
                    <div key={item.adCampaignMetaId} style={{ padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white', opacity: disabled ? 0.5 : 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{item.name}</span>
                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>최대 {fmt(item.cancelableAmount)}원</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="number" min="0" max={item.cancelableAmount}
                          value={val}
                          onChange={(e) => handleAmountChange(item.adCampaignMetaId, e.target.value)}
                          disabled={disabled}
                          className="cancel-amount-input"
                          data-testid={`amount-input-${item.adCampaignMetaId}`}
                          style={{ ...inputStyle, flex: 1 }}
                        />
                        <span style={{ fontSize: '12px', color: '#9ca3af', flexShrink: 0 }}>원</span>
                        <button onClick={() => handleFull(item)} disabled={disabled}
                          style={{ ...smallBtnBase, background: '#ede9fe', color: '#6d28d9', border: 'none' }}>
                          전액
                        </button>
                        <button onClick={() => handleHalf(item)} disabled={disabled}
                          style={{ ...smallBtnBase, background: 'white', color: '#6b7280', border: '1px solid #e5e7eb' }}>
                          절반
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 합계 */}
              <div style={{ padding: '12px 16px', background: '#f5f3ff', borderRadius: '8px', marginBottom: '16px', border: '1px solid #ede9fe', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>총 취소금액</span>
                  <span style={{ fontSize: '18px', fontWeight: 700, color: '#4f46e5' }}>{fmt(totalCancelAmount)}원</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }}>
                  <span>취소 후 잔여 결제액</span>
                  <span style={{ fontWeight: 600, color: '#374151' }}>{fmt(Math.max(0, (cancelable.cancelableAmount || 0) - totalCancelAmount))}원</span>
                </div>
                {epRefund != null && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }}>
                    <span>EP 원복 예정</span><span style={{ fontWeight: 600, color: '#374151' }}>{fmt(epRefund)} P</span>
                  </div>
                )}
                {cpRefund != null && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280' }}>
                    <span>CP 원복 예정</span><span style={{ fontWeight: 600, color: '#374151' }}>{fmt(cpRefund)} P</span>
                  </div>
                )}
              </div>

              {/* 취소 사유 */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                  취소 사유 <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="취소 사유를 입력해주세요"
                  rows={3}
                  data-testid="reason-input"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none', color: '#1f2937', lineHeight: 1.6, margin: 0 }}
                />
              </div>
            </>
          ) : null}

          {error && (
            <div data-testid="error-message" style={{ marginTop: '12px', padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', fontSize: '13px' }}>
              {error}
            </div>
          )}
        </div>

        {/* 하단 버튼 */}
        <div style={{ padding: '12px 20px 16px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={() => { if (!submitting) onClose(); }} disabled={submitting}
            style={{ padding: '9px 16px', fontSize: '13px', fontWeight: 500, border: '1px solid #e5e7eb', borderRadius: '6px', background: 'white', color: '#6b7280', cursor: submitting ? 'not-allowed' : 'pointer' }}>
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={isDisabled}
            data-testid="submit-button"
            style={{ padding: '9px 20px', fontSize: '13px', fontWeight: 600, border: 'none', borderRadius: '6px', background: isDisabled ? '#e5e7eb' : '#6366f1', color: isDisabled ? '#9ca3af' : 'white', cursor: isDisabled ? 'not-allowed' : 'pointer' }}>
            {submitting ? '처리 중...' : '취소 확정'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CancelModal;
